import { Controller } from "@hotwired/stimulus"
import { TIMESHEET_DATA_TYPE } from "../helpers/timesheet_data_transfer"

// "Daten aus Stundenzetteln" beside the report form.
//
// - Reloads the panel's frame for the report date whenever it changes. The date picker
//   writes the hidden field and dispatches a bubbling `change` on it, which reaches this
//   wrapper like any other form change — so only that one field counts.
// - Takes an answer or a photo over into a field of the report, by drag and drop onto
//   the field or through the item's "Übernehmen" menu. Nothing is saved here: an answer
//   is written into the form's input, a photo joins the field's pending uploads (as a
//   copy the server makes, see CustomReportTimesheetDataController#copy_upload), and
//   both are saved with the report. A photo of a progress entry ("Baufortschritt-Fotos",
//   `data-source="progression"`) is copied the same way by #copy_progression_image.
//
// Targets (key, label, type) come from the server; a field the form hides (by its
// conditions, or behind a translation), shows read-only, or that cannot take the item (a
// text past its maximum length, a photo past the field's file size limit) is not offered.
//
// A photo counts as taken over while the form holds a copy of it — pending, staged after
// a failed save, or saved with the report. Each such row names its origin in
// `data-copied-source`, so the form is the one record of it (see #syncTaken).
export default class extends Controller {
  static targets = ["frame", "status"]
  static values = {
    url: String,
    copyUrl: String,
    progressionCopyUrl: String,
    targets: Array,
    messages: Object,
  }

  static DATE_FIELD = "custom_report[report_date]"
  static HIGHLIGHT = ["ring-2", "ring-green-400", "rounded-md"]

  connect() {
    // Uploads being copied right now; their rows reach the form once the copy is back.
    this.copying = new Set()
  }

  disconnect() {
    this.closeMenu()
  }

  dateChanged(event) {
    if (event.target.name !== this.constructor.DATE_FIELD) return
    if (!this.hasFrameTarget || !this.urlValue) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("report_date", event.target.value)
    // Turbo keeps the frame's src absolute once it loaded; compare resolved URLs.
    const current = this.frameTarget.getAttribute("src")
    if (current && new URL(current, window.location.origin).href === url.href) return

    this.frameTarget.src = url.pathname + url.search
  }

  frameLoaded() {
    this.syncTaken()
  }

  // Marks each listed photo by whether the form holds a copy of it. Runs when the panel
  // loads and whenever the form drops a copy (file-upload:copied-removed).
  syncTaken() {
    const taken = this.takenSourceIds()
    this.element.querySelectorAll("[data-takeover='upload']").forEach((item) => {
      const isTaken = taken.has(item.dataset.uploadId)
      item.querySelector("[data-role='taken']")?.classList.toggle("hidden", !isTaken)
      item.querySelector("[data-role='take-over']")?.classList.toggle("hidden", isTaken)
    })
  }

  takenSourceIds() {
    const ids = new Set(this.copying)
    this.element
      .querySelector("form")
      ?.querySelectorAll("[data-copied-source]:not([data-copied-source=''])")
      .forEach((row) => ids.add(row.dataset.copiedSource))
    return ids
  }

  // --- Drag and drop ----------------------------------------------------------------

  dragstart(event) {
    const item = event.currentTarget
    if (this.taken(item)) {
      event.preventDefault()
      return
    }

    this.dragItem = item
    event.dataTransfer.setData(TIMESHEET_DATA_TYPE, item.dataset.takeover)
    event.dataTransfer.effectAllowed = "copy"
    this.compatibleTargets(item).forEach(({ element }) =>
      element.classList.add(...this.constructor.HIGHLIGHT),
    )
  }

  dragend() {
    this.dragItem = null
    this.element
      .querySelectorAll("[data-field-key]")
      .forEach((element) => element.classList.remove(...this.constructor.HIGHLIGHT))
  }

  dragover(event) {
    if (!this.dragItem || !this.targetAt(event.target)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  drop(event) {
    const item = this.dragItem
    const target = item && this.targetAt(event.target)
    if (!target) return

    event.preventDefault()
    this.dragend()
    this.takeOver(item, target)
  }

  // The compatible target under the pointer, if any.
  targetAt(node) {
    const element = node?.closest?.("[data-field-key]")
    if (!element || !this.dragItem) return null

    return (
      this.compatibleTargets(this.dragItem).find((target) => target.element === element) || null
    )
  }

  // --- Menu -------------------------------------------------------------------------

  openMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const item = button.closest("[data-takeover]")
    this.closeMenu()
    if (!item) return

    this.menu = this.buildMenu(item, this.compatibleTargets(item))
    document.body.appendChild(this.menu)
    const rect = button.getBoundingClientRect()
    this.menu.style.top = `${rect.bottom + window.scrollY + 4}px`
    this.menu.style.left = `${Math.max(8, rect.right + window.scrollX - this.menu.offsetWidth)}px`
    this.menuButton = button
    button.setAttribute("aria-expanded", "true")
    this.menu.querySelector("button")?.focus()
  }

  closeMenu() {
    this.menu?.remove()
    this.menu = null
    this.menuButton?.setAttribute("aria-expanded", "false")
    this.menuButton = null
  }

  closeMenuOutside(event) {
    if (!this.menu || this.menu.contains(event.target)) return
    this.closeMenu()
  }

  buildMenu(item, targets) {
    const menu = document.createElement("div")
    menu.setAttribute("role", "menu")
    menu.setAttribute("aria-label", this.message("menu_title"))
    menu.className =
      "absolute z-50 min-w-48 max-w-72 rounded-md border border-gray-200 bg-white py-1 shadow-lg"

    const title = document.createElement("p")
    title.className = "px-3 py-1 text-xs font-medium text-gray-500"
    title.textContent = this.message("menu_title")
    menu.append(title)

    if (targets.length === 0) {
      const empty = document.createElement("p")
      empty.className = "px-3 py-1.5 text-sm text-gray-500"
      empty.textContent = this.message("no_targets")
      menu.append(empty)
      return menu
    }

    targets.forEach((target) => {
      const option = document.createElement("button")
      option.type = "button"
      option.setAttribute("role", "menuitem")
      option.className =
        "block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
      option.textContent = target.label
      option.addEventListener("click", () => {
        this.closeMenu()
        this.takeOver(item, target)
      })
      menu.append(option)
    })
    return menu
  }

  // --- Taking over ------------------------------------------------------------------

  // The report fields this item can go into: on the page, shown, editable and of a
  // type the item fits.
  compatibleTargets(item) {
    return this.targetsValue
      .map((target) => ({ ...target, element: this.fieldElement(target.key) }))
      .filter((target) => target.element && !this.hiddenField(target.element))
      .filter((target) => this.fits(item, target))
  }

  fieldElement(key) {
    const form = this.element.querySelector("form")
    return form?.querySelector(`[data-field-key="${key}"]`) || null
  }

  hiddenField(element) {
    return element.closest(".hidden, [hidden]") !== null
  }

  fits(item, target) {
    if (item.dataset.takeover === "upload") return this.fitsUpload(item, target)

    const input = this.inputFor(target)
    if (!input || this.locked(target, input)) return false
    return this.answerValue(item, target, input) !== null
  }

  fitsUpload(item, target) {
    if (this.taken(item)) return false
    if (!["image", "file"].includes(target.type)) return false
    if (target.type === "image" && item.dataset.image !== "true") return false

    // The field's own limit for a direct upload (file_upload_controller.js).
    const maxSize = Number(target.element.dataset.fileUploadMaxSizeValue)
    return !maxSize || Number(item.dataset.byteSize || 0) <= maxSize
  }

  // Read-only, disabled, or out of sight: the original of a translated text is hidden
  // while the account reads the translation. A date field posts through a hidden input,
  // so its lock sits on the visible half.
  locked(target, input) {
    if (input.readOnly || input.disabled || this.hiddenField(input)) return true

    const display = target.element.querySelector("[data-date-picker-target='display']")
    return display?.matches("[readonly], :disabled") || false
  }

  inputFor(target) {
    return target.element.querySelector(`[name="custom_report[field_data][${target.key}]"]`)
  }

  // What the field holds once the answer is in; null when it does not fit.
  answerValue(item, target, input) {
    const { sourceType, raw = "", display = "" } = item.dataset
    switch (target.type) {
      case "text":
        return this.textValue(input, display)
      case "number": {
        const number = raw.replace(",", ".")
        return number !== "" && !Number.isNaN(Number(number)) ? number : null
      }
      case "time":
        if (sourceType === "time") return raw.slice(0, 5)
        if (sourceType === "datetime") return raw.match(/[T ](\d{2}:\d{2})/)?.[1] || null
        return null
      case "date":
        return ["date", "datetime"].includes(sourceType) && /^\d{4}-\d{2}-\d{2}/.test(raw)
          ? raw.slice(0, 10)
          : null
      case "checkbox":
        return sourceType === "checkbox" ? String(["true", "1"].includes(raw)) : null
      default:
        return null
    }
  }

  // A text field keeps what it has; the answer is added to it, within its maximum length
  // (a value set from script is not held to `maxlength`).
  textValue(input, answer) {
    const current = input.value.trimEnd()
    const separator = input.tagName === "TEXTAREA" ? "\n" : "; "
    const value = current.trim() === "" ? answer : `${current}${separator}${answer}`
    return input.maxLength > 0 && value.length > input.maxLength ? null : value
  }

  takeOver(item, target) {
    if (item.dataset.takeover === "upload") {
      this.copyUpload(item, target)
    } else {
      this.writeAnswer(item, target)
    }
  }

  writeAnswer(item, target) {
    const input = this.inputFor(target)
    const value = input && this.answerValue(item, target, input)
    if (value === null || value === undefined) return

    if (target.type === "checkbox") {
      input.checked = value === "true"
    } else {
      input.value = value
    }
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
    this.confirm(target)
  }

  async copyUpload(item, target) {
    const uploader = this.application.getControllerForElementAndIdentifier(
      target.element,
      "file-upload",
    )
    if (!uploader) return

    const sourceId = item.dataset.uploadId
    this.copying.add(sourceId)
    this.syncTaken()
    try {
      const response = await fetch(this.copyUrlFor(item), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
        },
        body: JSON.stringify({ upload_id: sourceId, report_date: this.reportDate() }),
      })
      if (!response.ok) throw new Error(`copy_upload ${response.status}`)

      const copy = await response.json()
      uploader.addCopiedFile({
        signedId: copy.signed_id,
        sourceId: copy.source_id,
        filename: copy.filename,
        byteSize: copy.byte_size,
        caption: copy.caption,
        previewUrl: copy.preview_url,
        image: copy.image,
      })
      this.confirm(target)
    } catch (error) {
      this.announce(this.message("copy_failed"), true)
      console.error(error)
    } finally {
      this.copying.delete(sourceId)
      this.syncTaken()
    }
  }

  copyUrlFor(item) {
    return item.dataset.source === "progression" ? this.progressionCopyUrlValue : this.copyUrlValue
  }

  reportDate() {
    return this.element.querySelector(`[name="${this.constructor.DATE_FIELD}"]`)?.value || ""
  }

  taken(item) {
    return item.querySelector("[data-role='taken']:not(.hidden)") !== null
  }

  // Shows where the value went: the field lights up briefly, and the status line names it.
  confirm(target) {
    target.element.classList.add(...this.constructor.HIGHLIGHT)
    setTimeout(() => target.element.classList.remove(...this.constructor.HIGHLIGHT), 1500)
    this.announce(this.message("added").replace("%{field}", target.label), false)
  }

  announce(text, error) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-red-600", error)
    this.statusTarget.classList.toggle("text-green-700", !error)
  }

  message(key) {
    return this.messagesValue[key] || key
  }
}
